import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconArrowsRightLeft, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useBranches } from '../branches/branchesApi';
import { CreateTransferModal } from './CreateTransferModal';
import { useTransfers } from './inventoryApi';

export function TransfersPage() {
  const { t, i18n } = useTranslation();
  const { data: branches } = useBranches();
  const [branchId, setBranchId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useTransfers(branchId);

  const branchOptions = useMemo(
    () => [
      { value: '', label: t('inventory.transfer.allBranches') },
      ...(branches?.map((b) => ({ value: b.branchId, label: b.nameAr })) ?? []),
    ],
    [branches, t],
  );

  return (
    <Stack gap="md">
      <PageHeader
        title={t('nav.items.transfers')}
        actions={
          <Button
            color="wheels"
            leftSection={<IconPlus size={16} stroke={1.6} />}
            onClick={() => setCreateOpen(true)}
          >
            {t('inventory.transfer.create')}
          </Button>
        }
      />

      <Paper p="md" withBorder>
        <Select
          label={t('inventory.transfer.branchFilter')}
          data={branchOptions}
          value={branchId ?? ''}
          onChange={(v) => setBranchId(v || null)}
          w={260}
        />
      </Paper>

      {isLoading && <Group justify="center" py="xl"><Loader size="sm" /></Group>}
      {error && (
        <Alert color="red">
          {error instanceof Error ? error.message : t('inventory.transfer.loadFailed')}
        </Alert>
      )}
      {data && data.length === 0 && !isLoading && (
        <Alert color="gray">{t('inventory.transfer.empty')}</Alert>
      )}

      {data && data.length > 0 && (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('inventory.transfer.number')}</Table.Th>
                <Table.Th>{t('inventory.transfer.fromTo')}</Table.Th>
                <Table.Th>{t('inventory.transfer.transferredAt')}</Table.Th>
                <Table.Th className="text-end">{t('inventory.transfer.lineCount')}</Table.Th>
                <Table.Th>{t('inventory.transfer.notes')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((trf) => (
                <Table.Tr key={trf.stockTransferId}>
                  <Table.Td className="font-mono">{trf.displayNumber}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Badge variant="light" color="gray">{trf.fromBranchNameAr}</Badge>
                      <IconArrowsRightLeft size={14} stroke={1.6} />
                      <Badge variant="light" color="wheels">{trf.toBranchNameAr}</Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td className="font-numeric">
                    {formatDate(trf.transferredAt, i18n.language)}
                  </Table.Td>
                  <Table.Td className="font-numeric text-end">{trf.lines.length}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>{trf.notes ?? '—'}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <CreateTransferModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </Stack>
  );
}

function formatDate(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-PS' : 'en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    numberingSystem: 'latn',
  }).format(new Date(iso));
}
